import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useStatuses } from "@/context/StatusContext";
import { STATUS_COLORS, STATUS_COLOR_OPTIONS } from "@/lib/meta";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Lock, ArrowUp, ArrowDown } from "lucide-react";

const emptyForm = { label: "", color: "indigo", requires_note: false, rigger_hidden: false, board_hidden: false };

export default function JobStatuses() {
  const { statuses, refresh } = useStatuses();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ label: s.label, color: s.color, requires_note: s.requires_note, rigger_hidden: !!s.rigger_hidden, board_hidden: !!s.board_hidden });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/job-statuses/${editing.id}`, form);
        toast.success("Status updated");
      } else {
        await api.post("/job-statuses", form);
        toast.success("Status added");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/job-statuses/${deleteId}`);
      toast.success("Status deleted");
      setDeleteId(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setDeleteId(null);
    }
  };

  const move = async (s, dir) => {
    const sorted = [...statuses];
    const idx = sorted.findIndex((x) => x.id === s.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swap];
    try {
      await Promise.all([
        api.put(`/job-statuses/${a.id}`, { order: b.order }),
        api.put(`/job-statuses/${b.id}`, { order: a.order }),
      ]);
      refresh();
    } catch (err) {
      toast.error("Could not reorder");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Administration</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">Job Status Management</h1>
          <p className="text-zinc-500 mt-1 text-sm">Control the statuses shown across jobs, dashboards and reports.</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="new-status-button"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
        >
          <Plus size={18} /> New Status
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] divide-y divide-zinc-100">
        {statuses.map((s, i) => (
          <div key={s.id} data-testid={`status-row-${s.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="flex flex-col">
                <button onClick={() => move(s, -1)} disabled={i === 0} className="text-zinc-300 hover:text-zinc-700 disabled:opacity-30">
                  <ArrowUp size={14} />
                </button>
                <button onClick={() => move(s, 1)} disabled={i === statuses.length - 1} className="text-zinc-300 hover:text-zinc-700 disabled:opacity-30">
                  <ArrowDown size={14} />
                </button>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border rounded-full ${STATUS_COLORS[s.color] || STATUS_COLORS.zinc}`}>
                {s.label}
              </span>
              {s.requires_note && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                  Note required
                </span>
              )}
              {s.rigger_hidden && (
                <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5">
                  Hidden from riggers
                </span>
              )}
              {s.board_hidden && (
                <span data-testid={`status-board-hidden-${s.id}`} className="text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-1.5 py-0.5">
                  Hidden from Home board
                </span>
              )}
              {s.is_system && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500">
                  <Lock size={11} /> System
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => openEdit(s)} data-testid={`edit-status-${s.id}`} className="text-zinc-400 hover:text-brand transition-colors">
                <Pencil size={16} />
              </button>
              <button onClick={() => setDeleteId(s.id)} data-testid={`delete-status-${s.id}`} className="text-zinc-400 hover:text-red-600 transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {editing ? "Edit Status" : "New Status"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Label</label>
              <input
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="status-label-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-2">Colour</label>
              <div className="flex flex-wrap gap-2" data-testid="status-color-picker">
                {STATUS_COLOR_OPTIONS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`status-color-${c}`}
                    className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider border rounded-full ${STATUS_COLORS[c]} ${
                      form.color === c ? "ring-2 ring-offset-1 ring-zinc-900" : ""
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800 cursor-pointer">
              <input
                type="checkbox"
                checked={form.requires_note}
                onChange={(e) => setForm({ ...form, requires_note: e.target.checked })}
                data-testid="status-requires-note"
                className="accent-brand"
              />
              Require a note when a job is set to this status
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800 cursor-pointer">
              <input
                type="checkbox"
                checked={form.rigger_hidden}
                onChange={(e) => setForm({ ...form, rigger_hidden: e.target.checked })}
                data-testid="status-rigger-hidden"
                className="accent-brand"
              />
              Hide from riggers (riggers can't select this status)
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800 cursor-pointer">
              <input
                type="checkbox"
                checked={form.board_hidden}
                onChange={(e) => setForm({ ...form, board_hidden: e.target.checked })}
                data-testid="status-board-hidden"
                className="accent-brand"
              />
              Hide from Home due-date board (and the overdue number beside Home)
            </label>
            <DialogFooter>
              <button
                type="submit"
                disabled={saving}
                data-testid="status-submit-button"
                className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Save Changes" : "Create Status"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this status?</AlertDialogTitle>
            <AlertDialogDescription>
              Statuses currently used by jobs cannot be deleted. Deleting a system
              status (e.g. In Progress or Completed) will disable its automatic
              behaviour such as the completion timer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-status" className="bg-red-600 hover:bg-red-700 rounded-none">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
